import * as atlas from "azure-maps-control"
import type {StyleImageMetadata} from "azure-maps-control";
import {IEventChannel} from "@esWebAPI/es/model/Models";

/**
 * Wrapper class around the Azure ImageSpriteManager interface
 * This class adds a map to know what image have been added to the Azure map before they are fully loaded.
 * {@link https://docs.microsoft.com/en-us/javascript/api/azure-maps-control/atlas.imagespritemanager?view=azure-maps-typescript-latest#azure-maps-control-atlas-imagespritemanager-add}
 */
export class AzureImageSpriteManager {

    private readonly _imageSpriteManager: atlas.ImageSpriteManager;
    private readonly _sprites: Map<string, Promise<void>>;
    private readonly _eventChannel: IEventChannel;
    /**
     * @param {atlas.Map} map The AzureMap
     * @param eventChannel
     */
    constructor(map: atlas.Map, eventChannel: IEventChannel) {
        this._imageSpriteManager = map.imageSprite;
        this._eventChannel = eventChannel;
        this._imageSpriteManager.imageLoadTimeout = 20000; // 20 seconds just in case
        this._sprites = new Map<string, Promise<void>>; //Map of sprite deferred <string, Promise<void>>
    }

    public imageIds() {
        return Array.from(this._sprites.keys());
    }

    /**
     * Add an icon image to the map's image sprite for use with symbols and patterns.<br />
     * https://docs.microsoft.com/en-us/javascript/api/azure-maps-control/atlas.imagespritemanager?view=azure-maps-typescript-latest#azure-maps-control-atlas-imagespritemanager-add
     * @param {string} id The image's id. If the specified id matches the id of a previously added image the new image will be ignored.
     * @param {string} data The image url to add to the map's sprite.
     * @param meta
     * @return {Promise<void>}
     */
    public add(id: string, data: string | HTMLImageElement| ImageData, meta?: StyleImageMetadata): Promise<void> {
        if (!this._sprites.has(id)){
            const promise = this._imageSpriteManager.add(id, data, meta);
            this._sprites.set(id, promise);
            return promise;
        }
        //console.log("AzureImageSpriteManager#add", id);
        return this._sprites.get(id) || Promise.resolve();
    }

    public async addFromStyle(style: any): Promise<string> {
        const imageId = `es_${style.symbolTypeId}_${style.size}_${style.color}`;
        let url = `${es.restUrl}/style?style=(%24type~%27SymbolStyle%27~symbolTypeId~${style.symbolTypeId}~size~${style.size}~color~${style.color})&width=${style.size}&height=${style.size}`;
        if (es.isPublicAccess) {
            url += `&TENANT_ID=${es.tenantId}`;
        }
        await this.add(imageId, url);
        return imageId
    }

    public addTemplate(templateName: string, template: string, override: boolean) {
        //console.log("AzureImageSpriteManager.addTemplate", templateName /*, template, override*/);
        atlas.addImageTemplate(templateName, template, override);
    }

    /**
     * Add an icon image to the map's image sprite for use with symbols and patterns.<br />
     * https://learn.microsoft.com/en-us/javascript/api/azure-maps-control/atlas.imagespritemanager?view=azure-maps-typescript-latest#azure-maps-control-atlas-imagespritemanager-createfromtemplate
     * @param {string} id The image's id. If the specified id matches the id of a previously added image the new image will be ignored.
     * @param {string} templateName The name of the template to use.
     * @param {string} color The primary color value. Default: #1A73AA
     * @param {string} secondaryColor A secondary color value. Default: white
     * @param {number} scale Specifies how much to scale the template. For best results, scale the icon to the maximum size you want to display it on the map, then use the symbol layers icon size option to scale down if needed. This will reduce blurriness due to scaling. Default: 1
     * @return {Promise<void>}
     */
    public createFromTemplate(id: string, templateName: string, color?: string, secondaryColor? :string, scale?: number): Promise<void> {

        const templates = atlas.getAllImageTemplateNames();
        if (!templates.includes(templateName)) {
            console.warn("Template not found", templateName);
            templateName ='pin';
        }

        if (!this._sprites.has(id)){
            //console.log("createFromTemplate : ", id, templateName, color, secondaryColor, scale);
            this._sprites.set(id, this._imageSpriteManager.createFromTemplate(id, templateName, color, secondaryColor, scale));
        }
        return this._sprites.get(id) || Promise.reject("Sprite not found " + id);
    }

    public hasImage(imageId: string): boolean {
        return this._sprites.has(imageId);
    }

    public get eventChannel(): IEventChannel {
        return this._eventChannel;
    }
}
